import React, { useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { HERO } from '../../data/content';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const F = HERO.fields;

const fieldCls =
  'w-full bg-transparent border-b border-teal/25 py-3 text-teal placeholder:text-teal/35 ' +
  'font-light text-base md:text-lg outline-none transition-colors duration-300 ' +
  'focus:border-klein';

const Label = ({ children }) => (
  <span className="block text-[10px] tracking-[0.28em] uppercase text-teal/50 mb-1 font-medium">
    {children}
  </span>
);

export const JoinForm = () => {
  const [form, setForm] = useState({
    name: '',
    art_form: '',
    email: '',
    contact_number: '',
    work_links: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.art_form || !form.email || !form.contact_number) {
      toast('Please complete your name, art form, email and contact number.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/join`, form);
      setDone(true);
      toast('You\u2019re in. Welcome to the Unscene.');
    } catch (err) {
      toast('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="join-form-wrapper" className="w-full">
      <AnimatePresence mode="wait">
        {done ? (
          <motion.div
            key="done"
            data-testid="join-success"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="border-t border-teal/20 pt-10"
          >
            <p className="font-serif italic text-3xl md:text-4xl text-teal leading-tight">
              Your name is on the list.
            </p>
            <p className="mt-3 text-teal/60 font-light">
              {'We\u2019ll be in touch at '}
              {form.email}.
            </p>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            onSubmit={onSubmit}
            data-testid="join-form"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0, y: -10 }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7"
          >
            <div className="sm:col-span-2">
              <Label>{F.name.label}</Label>
              <input
                data-testid="input-name"
                className={fieldCls}
                placeholder={F.name.placeholder}
                value={form.name}
                onChange={set('name')}
              />
            </div>

            <div>
              <Label>{F.artForm.label}</Label>
              <select
                data-testid="input-artform"
                className={`${fieldCls} appearance-none cursor-pointer ${
                  form.art_form ? '' : 'text-teal/35'
                }`}
                value={form.art_form}
                onChange={set('art_form')}
              >
                <option value="" disabled>
                  Filmmaking / Photography / Writing / Music / Design / Other
                </option>
                {F.artForm.options.map((o) => (
                  <option key={o} value={o} className="text-teal">
                    {o}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label>{F.email.label}</Label>
              <input
                data-testid="input-email"
                type="email"
                className={fieldCls}
                placeholder={F.email.placeholder}
                value={form.email}
                onChange={set('email')}
              />
            </div>

            <div>
              <Label>{F.contact.label}</Label>
              <input
                data-testid="input-contact"
                className={fieldCls}
                placeholder={F.contact.placeholder}
                value={form.contact_number}
                onChange={set('contact_number')}
              />
            </div>

            <div>
              <Label>{F.work.label}</Label>
              <input
                data-testid="input-work"
                className={fieldCls}
                placeholder={F.work.placeholder}
                value={form.work_links}
                onChange={set('work_links')}
              />
            </div>

            <div className="sm:col-span-2 pt-3">
              <button
                type="submit"
                data-testid="submit-join-form"
                disabled={submitting}
                className="group relative inline-flex items-center gap-3 bg-klein text-cream px-9 py-4 text-xs tracking-[0.22em] uppercase font-medium overflow-hidden disabled:opacity-60"
              >
                <span className="relative z-10">
                  {submitting ? 'Sending\u2026' : HERO.formHeading}
                </span>
                <span className="relative z-10 transition-transform duration-500 group-hover:translate-x-1">
                  &rarr;
                </span>
                <span className="absolute inset-0 bg-teal translate-y-full transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-y-0" />
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};
